import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Boxes, TrendingUp, TrendingDown, IndianRupee, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MetricCard({ title, value, subtitle, icon: Icon, trend }) {
  return (
    <div data-testid={`metric-${title.toLowerCase().replace(/ /g, '-')}`} className="bg-white border border-concrete-200 shadow-sm hover:shadow-md transition-shadow duration-200 p-6 rounded-md relative overflow-hidden group">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-600 mb-2">{title}</p>
          <h3 className="font-manrope font-bold text-3xl text-concrete-900 tabular-nums">{value}</h3>
          {subtitle && <p className="font-inter text-sm text-concrete-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-md ${
          trend === 'up' ? 'bg-green-50' : trend === 'down' ? 'bg-red-50' : 'bg-brick-50'
        }`}>
          <Icon className={`h-6 w-6 ${
            trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-brick-600'
          }`} />
        </div>
      </div>
      <div className="absolute bottom-0 right-0 w-24 h-24 bg-brick-600 opacity-5 rounded-tl-full transform translate-x-8 translate-y-8" />
    </div>
  );
}

export default function Dashboard() {
  const [stock, setStock] = useState(null);
  const [todayProduction, setTodayProduction] = useState([]);
  const [todaySales, setTodaySales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) setRefreshing(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Not authenticated');
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      const today = new Date().toISOString().split('T')[0];

      const [stockRes, productionRes, salesRes] = await Promise.all([
        axios.get(`${API}/stock`, { headers }),
        axios.get(`${API}/production?date=${today}`, { headers }),
        axios.get(`${API}/sales?date=${today}`, { headers })
      ]);

      setStock(stockRes.data);
      setTodayProduction(productionRes.data);
      setTodaySales(salesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data. Please refresh.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    fetchData(true);
  };

  const todayProdTotal = todayProduction.reduce((acc, p) => acc + p.fly_ash + p.interlock_6 + p.interlock_8, 0);
  const todaySalesTotal = todaySales.reduce((acc, s) => acc + s.fly_ash + s.interlock_6 + s.interlock_8, 0);
  const todayRevenue = todaySales.reduce((acc, s) => acc + s.amount_paid, 0);

  if (loading) {
    return <div className="flex items-center justify-center h-96">
      <p className="font-inter text-concrete-600">Loading dashboard...</p>
    </div>;
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="font-manrope font-bold text-4xl text-concrete-900 tracking-tight" data-testid="dashboard-title">Dashboard</h1>
          <p className="font-inter text-concrete-600 mt-2">Overview of production, sales, and stock</p>
        </div>
        <Button
          onClick={handleRefresh}
          variant="outline"
          disabled={refreshing}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <MetricCard
          title="Fly Ash Stock"
          value={stock ? stock.fly_ash.toLocaleString() : 0}
          subtitle="Fly Ash Bricks"
          icon={Boxes}
        />
        <MetricCard
          title='6" Interlock Stock'
          value={stock ? stock.interlock_6.toLocaleString() : 0}
          subtitle="6 Inch Interlock"
          icon={Boxes}
        />
        <MetricCard
          title='8" Interlock Stock'
          value={stock ? stock.interlock_8.toLocaleString() : 0}
          subtitle="8 Inch Interlock"
          icon={Boxes}
        />
        <MetricCard
          title="Cement Stock"
          value={stock ? `${stock.cement.toLocaleString()} kg` : '0 kg'}
          subtitle="Available Cement"
          icon={Boxes}
        />
        <MetricCard
          title="Today Revenue"
          value={`₹${todayRevenue.toLocaleString()}`}
          subtitle="Total collection"
          icon={IndianRupee}
        />
      </div>

      {/* Custom Materials Stock */}
      {stock?.custom_materials && Object.keys(stock.custom_materials).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Object.entries(stock.custom_materials).map(([name, qty]) => (
            <div key={name} className="bg-purple-50 border border-purple-200 p-4 rounded-md">
              <p className="font-manrope font-semibold uppercase tracking-wider text-xs text-purple-600 mb-1">{name}</p>
              <h3 className="font-manrope font-bold text-2xl text-purple-700 tabular-nums">{qty.toLocaleString()}</h3>
              <p className="font-inter text-xs text-purple-500">Custom Material</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div data-testid="stock-breakdown" className="bg-white border border-concrete-200 shadow-sm rounded-md p-6">
          <h2 className="font-manrope font-bold text-xl text-concrete-900 mb-6">Current Stock</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center pb-3 border-b border-concrete-200">
              <span className="font-inter text-concrete-700">Fly Ash Bricks</span>
              <span className="font-inter font-semibold text-brick-600 tabular-nums">{stock?.fly_ash.toLocaleString() || 0}</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b border-concrete-200">
              <span className="font-inter text-concrete-700">6" Interlock</span>
              <span className="font-inter font-semibold text-brick-600 tabular-nums">{stock?.interlock_6.toLocaleString() || 0}</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b border-concrete-200">
              <span className="font-inter text-concrete-700">8" Interlock</span>
              <span className="font-inter font-semibold text-brick-600 tabular-nums">{stock?.interlock_8.toLocaleString() || 0}</span>
            </div>
            {/* Custom Materials */}
            {stock?.custom_materials && Object.entries(stock.custom_materials).map(([name, qty]) => (
              <div key={name} className="flex justify-between items-center pb-3 border-b border-concrete-200">
                <span className="font-inter text-purple-700">{name}</span>
                <span className="font-inter font-semibold text-purple-600 tabular-nums">{qty.toLocaleString()}</span>
              </div>
            ))}
            <div className="flex justify-between items-center">
              <span className="font-inter text-concrete-700">Cement</span>
              <span className="font-inter font-semibold text-brick-600 tabular-nums">{stock?.cement?.toLocaleString() || 0} kg</span>
            </div>
          </div>
        </div>

        <div data-testid="recent-production" className="bg-white border border-concrete-200 shadow-sm rounded-md p-6">
          <h2 className="font-manrope font-bold text-xl text-concrete-900 mb-6">Today's Production</h2>
          {todayProduction.length === 0 ? (
            <p className="font-inter text-concrete-500 text-center py-8">No production recorded today</p>
          ) : (
            <div className="space-y-3">
              {todayProduction.slice(0, 5).map((prod, idx) => (
                <div key={prod.id} className="text-sm font-inter text-concrete-700 pb-2 border-b border-concrete-100 last:border-0">
                  <div className="flex justify-between">
                    <span>Entry #{idx + 1}</span>
                    <span className="font-semibold text-brick-600 tabular-nums">{prod.fly_ash + prod.interlock_6 + prod.interlock_8}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div data-testid="recent-sales" className="bg-white border border-concrete-200 shadow-sm rounded-md p-6">
          <h2 className="font-manrope font-bold text-xl text-concrete-900 mb-6">Today's Sales</h2>
          {todaySales.length === 0 ? (
            <p className="font-inter text-concrete-500 text-center py-8">No sales recorded today</p>
          ) : (
            <div className="space-y-3">
              {todaySales.slice(0, 5).map((sale) => (
                <div key={sale.id} className="text-sm font-inter pb-2 border-b border-concrete-100 last:border-0">
                  <div className="flex justify-between items-start">
                    <span className="text-concrete-700">{sale.customer_name}</span>
                    <span className="font-semibold text-brick-600 tabular-nums">₹{sale.amount_paid.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}